import React, { useState, useEffect, SyntheticEvent, useCallback, useRef } from 'react';
import {
	Dialog,
	DialogContent,
	DialogActions,
	Button,
	IconButton,
	DialogTitle,
	Checkbox,
	TextField,
	Box,
	FilterOptionsState,
	Autocomplete,
} from '@mui/material';
import PrimaryButton from '../button/PrimaryButton';
import Text from '../text';
import ApiService from 'utils/services/ApiService';
import { usePopupMessage } from '../../hooks/SendPopUpMessage';
import { useSelector, useDispatch } from 'react-redux';
import { IReduxState } from 'utils/models/redux-states';
import { responseEmitter, sendAnalyzerCommand } from 'utils/usb/USBConnector';
import CancelConfirmationDialog from '../dialog/Cancel';
import Spinner from '../dialog/Spinner';
import { getTestResults, saveTestResults } from 'utils/services/idbService';
import { setUpdateReduxData } from 'utils/redux/reducers/updateDataReducer';
import { setAllSitesData } from 'utils/redux/reducers/allSitesLoadedReducer';
import dayjs from 'dayjs';
import { TabbableAutocomplete } from '../Dropdowns/TabbableAutocomplete';
interface ImportModalProps {
	onClose: (importedResults?: number) => void;
}

interface BCASite {
	id: number;
	name: string;
	sampleLocationAllVOs?: {
		id: number;
		name: string;
	}[];
}

interface BCALocaton {
	id: number;
	name: string;
}

export interface DeviceResult {
	id: number;
	date: string;
	timestamp: number;
	testKit: string;
	imported: boolean;
	site: BCASite;
	location: BCALocaton | null;
	notes?: string;
	inputs: Record<string, string>;
	outputs: Record<string, string>;
}

interface RootState {
	allSitesLoaded: {
		allSitesData: BCASite[];
	};
}

interface SiteOption {
	name: string;
	id: number;
	inputValue?: string;
	locations: LocationOption[];
}

interface LocationOption {
	name: string;
	id: number;
	inputValue?: string;
	siteId?: number;
}

function populatingExistingDefaultLocations(allSitesCache: any[]): Record<number, number> {
	const siteIDToDefaultLocationIDMap: Record<number, number> = {};

	allSitesCache.forEach((site: any) => {
		if (site.sampleLocationAllVOs && site.sampleLocationAllVOs.length > 0) {
			const defaultLocation = site.sampleLocationAllVOs.find((loc: any) => loc.name === "Imported Tests");
			if (defaultLocation) {
				siteIDToDefaultLocationIDMap[site.id] = defaultLocation.id;
			}
		}
	});

	return siteIDToDefaultLocationIDMap;
}

function matchTestKit(incomingBCAKitName: string, defaultKitInfo: any): any {

	let kitDetails = null;
	let kitName = null;
	let hasSpecialFieldCases = false;

	let defaultKit = defaultKitInfo.find((kit: any) => kit.testKitName === incomingBCAKitName);

	if (defaultKit !== undefined) {
		kitDetails = defaultKit.fieldDescriptorAllVOs;
		kitName = defaultKit.testKitName;
	}

	let hardcodedTestKitMapping: Record<string, string> = {

		//LuminUltra org
		"Industrial (tATP only)": "Industrial",
		"Specialty (tATP only)": "Specialty",

		//AccuCount org
		"Fluids (ATP + AMP)": "AccuCount Fluid",
		"Fluid (ATP + AMP)": "AccuCount Fluid",
		"Fluids Basic": "AccuCount Fluid",
		"Fluid Basic": "AccuCount Fluid",
		"Fluid": "AccuCount Fluid",
		"Fluids": "AccuCount Fluid",
		"Solids Basic": "AccuCount Solids",
		"Solids (ATP + AMP)": "AccuCount Solids",
		"Solids": "AccuCount Solids",
		"Swab Basic": "AccuCount Swab",
		"Swab (ATP + AMP)": "AccuCount Swab",
		"Swab": "AccuCount Swab",
		"Coupon Basic": "AccuCount Coupon",
		"Coupon (ATP + AMP)": "AccuCount Coupon",
		"Coupon": "AccuCount Coupon",

		//LumiKem org
		"Water": "LumiKem Water",
		"Wastewater": "LumiKem Wastewater",
		"Process Lite": "LumiKem Process Lite",
		"Process Standard": "LumiKem Process",
		"Process tATP Only": "LumiKem Process",
		"Pigments Standard": "LumiKem Pigments",
		"Pigments tATP Only": "LumiKem Pigments",
		"Wastewater Adv.": "LumiKem Wastewater",
		"Organic": "LumiKem Organic"

	}

	let specialFieldCaseKits = [
		"Industrial (tATP only)",
		"Specialty (tATP only)",
		"Fluids Basic",
		"Fluid Basic",
		"Solids Basic",
		"Swab Basic",
		"Coupon Basic",
		"Wastewater",
		"Process tATP Only",
		"Pigments tATP Only",
	]

	if (kitDetails === null) {
		kitDetails = defaultKitInfo ? defaultKitInfo.find((kit: any) => kit.testKitName === hardcodedTestKitMapping[incomingBCAKitName]).fieldDescriptorAllVOs : null;
		kitName = defaultKitInfo ? defaultKitInfo.find((kit: any) => kit.testKitName === hardcodedTestKitMapping[incomingBCAKitName]).testKitName : null;
		if (kitDetails !== null && specialFieldCaseKits.includes(incomingBCAKitName)) {
			hasSpecialFieldCases = true;
		}
	}

	return {
		fieldDescriptorAllVOs: kitDetails,
		name: kitName,
		hasSpecialFieldCases: hasSpecialFieldCases
	}
}

function processSpecialFieldCases(valueDatas: any[], testKitName: string): any[] {

	let returnData = valueDatas;

	if (testKitName === "Industrial (tATP only)" || testKitName === "Process tATP Only") {
		returnData = returnData.concat({
			outputId: "dATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Output"
		},
		{
			outputId: "cATP",
			outputValue: valueDatas.find((value) => value.outputId === "tATP")?.outputValue ?? 0,
			outputAlarm: 10,
			type: "Output"
		},
		{
			outputId: "RLUdATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Output"
		},
		{
			outputId: "BSI",
			outputValue: 0,
			outputAlarm: 10,
			type: "Output"
		})

	} else if (testKitName === "Specialty (tATP only)" || testKitName === "Pigments tATP Only") {
		returnData = returnData.concat({
			outputId: "VOLdATP",
			outputValue: 1,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "dATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Output"
		},
		{
			outputId: "cATP",
			outputValue: valueDatas.find((value) => value.outputId === "tATP")?.outputValue ?? 0,
			outputAlarm: 10,
			type: "Output"
		},
		{
			outputId: "RLUdATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Output"
		},
		{
			outputId: "BSI",
			outputValue: 0,
			outputAlarm: 10,
			type: "Output"
		})

	} else if (testKitName === "Fluids Basic" || testKitName === "Fluid Basic") {
		returnData = returnData.concat({
			outputId: "RLUATP2",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "RLUAMP1",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "RLUcAMPcATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "bRLUAMP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "bRLUATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		})

	} else if (testKitName === "Solids Basic") {
		returnData = returnData.concat({
			outputId: "RLUATP2",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "RLUAMP1",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "SolidsRLUtAMPtATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "bRLUAMP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "bRLUATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		})

	} else if (testKitName === "Swab Basic") {
		returnData = returnData.concat({
			outputId: "RLUATP2",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "RLUAMP1",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "SwabRLUtAMPtATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "bRLUAMP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "bRLUATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		})

	} else if (testKitName === "Coupon Basic") {
		returnData = returnData.concat({
			outputId: "RLUATP2",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "RLUAMP1",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "CouponRLUtAMPtATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "bRLUAMP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "bRLUATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		})

	} else if (testKitName === "Wastewater") {
		returnData = returnData.concat({
			outputId: "RLUfbATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "RLUagATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		},
		{
			outputId: "VOLagATP",
			outputValue: 0,
			outputAlarm: 10,
			type: "Input"
		})

	}

	return returnData;

}

function processValue(value: string, outputId: string, testKit: string): string {
	if (value === '0.00e+00') {
		return '0';
	}
	return value;
}

function processFieldAlarm(value: string, outputId: string, kitId: number | null, locationId: number, allSitesCache: any, defaultKitInfo: any): number {

	if (value === 'N/A' || kitId === null) {
		return 10;
	}

	let customAlarms = null;
	let defaultAlarms = null;

	//check if location exists and has custom alarms first
	customAlarms = allSitesCache
		.find((site: any) => site.sampleLocationAllVOs.some((loc: any) => loc.id === locationId))
		?.sampleLocationAllVOs.find((loc: any) => loc.id === locationId)
		?.customAlarmVOs?.find((alarm: any) => alarm.outputId === outputId && alarm.testKitId === kitId) 
		?? null;

	if (customAlarms !== null) {
		return applyAlarms(value, customAlarms);
	}

	//default to default alarms
	defaultAlarms = defaultKitInfo
		.find((kit: any) => kit.id === kitId)
		?.fieldDescriptorAllVOs.find((field: any) => field.outputId === outputId)
		?? null;

	if (defaultAlarms !== null) {
		return applyAlarms(value, defaultAlarms);
	}

	return 10;

}

function applyAlarms(value: string, alarms: any): number {

		let hiValue = alarms.hiAlarmValue;
		let hiHiValue = alarms.hiHiAlarmValue;
		let loValue = alarms.loAlarmValue;
		let loLoValue = alarms.loLoAlarmValue;

		if (
			hiValue === undefined &&
			hiHiValue === undefined &&
			loValue === undefined &&
			loLoValue === undefined
		) {
			return 10;
		}

		let isOneWayAlarm =
			loValue === undefined && loLoValue === undefined;
		let isTwoWayAlarm =
			loValue !== undefined || loLoValue !== undefined;

		if (isTwoWayAlarm) {
			if (loLoValue !== undefined && value <= loLoValue) {
				return 1;
			} else if (loValue !== undefined && value <= loValue) {
				return 2;
			}
		}

		if (isOneWayAlarm || isTwoWayAlarm) {
			if (hiHiValue !== undefined && value >= hiHiValue) {
				return 1;
			} else if (hiValue !== undefined && value >= hiValue) {
				return 2;
			}
		}

		return 3;
}

function processFieldID(name: string, kitDetails: any): string {

	let fieldID = kitDetails.find((field: any) => field.outputName === name)?.outputId ?? null;
	if (fieldID === null) {
		const trimmedUnitName = name.replace(/ \(.+\)/, '').trim(); //Remove units in parentheses and try again
		fieldID = kitDetails.find((field: any) => field.outputName === trimmedUnitName)?.outputId ?? null;
		if (name === "RLU tAMP + tATP") {
			fieldID = kitDetails.find((field: any) => field.outputName === "RLU tAMP+tATP")?.outputId ?? null;
		} else if (name === "RLU cAMP + cATP" || name === "RLU cAMP+cATP") {
			fieldID = kitDetails.find((field: any) => field.outputName === "RLU cAMP+cATP")?.outputId ?? null;
		}
		if (fieldID === null) {
			fieldID = kitDetails.find((field: any) => field.outputName === "tATP Volume")?.outputId ?? null; //Specialty Kit fix
		}
	}

	return fieldID ?? name;
}

function handleDownloadCSV(csvContent: string, siteName: string) {
	const blob = new Blob([csvContent], {
		type: 'text/csv;charset=utf-8;',
	});
	const url = URL.createObjectURL(blob);

	const link = document.createElement('a');
	link.href = url;
	link.setAttribute(
		'download',
		`LuminUltra Data Export - ${siteName} - ${dayjs().format('YYYYMMDD')}.csv`
	);
	document.body.appendChild(link);
	link.click();
	link.remove();

	// Release the URL
	URL.revokeObjectURL(url);
};


interface ResultRowProps {
	result: DeviceResult;
	allSiteNames: SiteOption[];
	locationsBySiteName: Map<string, LocationOption[]>;
	newLocationList: LocationOption[];
	filterSiteOptions: (options: SiteOption[], params: FilterOptionsState<SiteOption>) => SiteOption[];
	filterLocationOptions: (options: LocationOption[], params: FilterOptionsState<LocationOption>, siteId?: number) => LocationOption[];
	onUpdateField: <K extends keyof DeviceResult>(id: number, field: K, value: DeviceResult[K]) => void;
	onUpdateSiteOptionList: (value: any) => void;
	onUpdateLocationOptionList: (value: any, siteId: any) => void;
}

const ResultRow = React.memo(({
	result,
	allSiteNames,
	locationsBySiteName,
	newLocationList,
	filterSiteOptions,
	filterLocationOptions,
	onUpdateField,
	onUpdateSiteOptionList,
	onUpdateLocationOptionList,
}: ResultRowProps) => {
	const [siteInputValue, setSiteInputValue] = useState('');
	const [locationInputValue, setLocationInputValue] = useState('');
	// Stable per-row callbacks — won't recreate unless result.id changes
	const locationInputRef = useRef<HTMLInputElement | null>(null);
	const notesInputRef = useRef<HTMLInputElement | null>(null);
	const handleImportChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		onUpdateField(result.id, "imported", e.target.checked);
	}, [result.id, onUpdateField]);
	useEffect(() => {
		if (!result.location || result.location.id === -1) {
			setLocationInputValue('');
		}
	}, [result.location]);

	const handleSiteChange = useCallback((_: any, newValue: SiteOption | string | null) => {
	if (typeof newValue === 'string') {
		onUpdateField(result.id, 'site', { name: newValue, id: -1 });
	} else if (newValue && 'name' in newValue) {
		if ('inputValue' in newValue) {
			const allOptions = filterSiteOptions(allSiteNames, {
				inputValue: newValue.name,
				getOptionLabel: (o) => o.name,
			});
			const existing = allOptions.find(
				(o) => !('inputValue' in o) && o.name.toLowerCase() === newValue.name.toLowerCase()
			);
			if (existing) {
				onUpdateSiteOptionList(existing);
				onUpdateField(result.id, 'site', { name: existing.name, id: existing.id });
				return;
			}
		}
		const newSite: SiteOption = { ...newValue, locations: newValue.locations ?? [] };
		onUpdateSiteOptionList(newSite);
		onUpdateField(result.id, 'site', { name: newSite.name, id: newSite.id });
	} else {
		onUpdateField(result.id, 'site', { name: '', id: -1 });
	}
	}, [result.id, allSiteNames, filterSiteOptions, onUpdateField, onUpdateSiteOptionList]);
	const locationOptions = [
		...(locationsBySiteName.get(result.site?.name ?? '') ?? []),
		...newLocationList.filter(loc => loc.siteId === result.site?.id)
	];

	const handleLocationChange = useCallback((_: any, newValue: LocationOption | string | null) => {
		if (typeof newValue === 'string') {
			const existing = locationOptions.find(loc => loc.name.toLowerCase() === newValue.toLowerCase());
			if (existing) 
				onUpdateField(result.id, 'location', { name: existing.name, id: existing.id });
			else {
				const newLoc = { name: newValue, id: new Date().getTime(), siteId: Number(result.site?.id) };
				onUpdateLocationOptionList(newLoc, result.site?.id);
				onUpdateField(result.id, 'location', { name: newValue, id: newLoc.id });
			}
		} else if (newValue && 'name' in newValue) { //they clicked Add, extract values out
			const cleanValue = { name: newValue.name, id: newValue.id };
			if ('inputValue' in newValue) 
				onUpdateLocationOptionList(cleanValue, result.site?.id);
			onUpdateField(result.id, 'location', cleanValue);
		} else {
			onUpdateField(result.id, 'location', null);
		}
	}, [result.id, result.site?.id, locationOptions, onUpdateField, onUpdateLocationOptionList]);

	const handleNotesChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		onUpdateField(result.id, "notes", e.target.value);
	}, [result.id, onUpdateField]);

	// Stable filterLocationOptions wrapper bound to this row's result
	const handleFilterLocationOptions = useCallback((options: LocationOption[], params: FilterOptionsState<LocationOption>) => {
		return filterLocationOptions(options, params, result.site?.id);
	}, [filterLocationOptions, result.site?.id]); // only re-bind when site changes

	const siteValue = (result.site && result.site.id !== -1 && result.site.name !== '')
		? { name: result.site.name, id: result.site.id, locations: [] }
		: null;

	return (
		<tr style={{ borderBottom: '1px solid #e0e0e0', background: result.imported ? '#F4FCFF' : '' }}>
			<td style={{ padding: '8px', textAlign: 'center' }}>
				<Checkbox checked={result.imported} onChange={handleImportChange} style={{ zIndex: '2' }} />
			</td>
			<td style={{ padding: '8px', textAlign: 'center' }}>{result.date}</td>
			<td style={{ padding: '8px', textAlign: 'center' }}>{result.testKit}</td>
			<td style={{ padding: '8px', textAlign: 'center' }}>
				<Box minWidth={200}>
					<TabbableAutocomplete<SiteOption>
						value={siteValue}
						inputValue={siteInputValue}
						options={allSiteNames}
						filterOptions={filterSiteOptions}
						onChange={handleSiteChange}
						onInputChange={setSiteInputValue}
						nextFocusRef={locationInputRef}
						disabled={!result.imported}
					/>
				</Box>
			</td>
			<td style={{ padding: '8px', textAlign: 'center' }}>
				<Box minWidth={200}>
					<TabbableAutocomplete<LocationOption>
						value={result.location ?? null}
						inputValue={locationInputValue}
						options={locationOptions}
						filterOptions={handleFilterLocationOptions}
						onChange={handleLocationChange}
						onInputChange={setLocationInputValue}
						disabled={!result.imported || !result.site}
						freeSolo
						placeholder="Select a Site first"
						inputRef={locationInputRef}
						nextFocusRef={notesInputRef}
					/>
				</Box>
			</td>
			<td style={{ padding: '8px', textAlign: 'center' }}>
				<Box minWidth={120}>
					<TextField
						value={result.notes ?? ''}
						onChange={handleNotesChange}
						disabled={!result.imported}
						size="small"
						variant="outlined"
						inputRef={notesInputRef}
					/>
				</Box>
			</td>
		</tr>
	);
}, (prev, next) =>
	prev.result === next.result &&
	prev.allSiteNames === next.allSiteNames &&
	prev.locationsBySiteName === next.locationsBySiteName &&
	prev.newLocationList === next.newLocationList &&
	prev.filterSiteOptions === next.filterSiteOptions &&
	prev.filterLocationOptions === next.filterLocationOptions &&
	prev.onUpdateField === next.onUpdateField
);





const ImportModal: React.FC<ImportModalProps> = ({
	onClose,
}) => {
	const allSitesList = useSelector<RootState, BCASite[]>(state => state.allSitesLoaded.allSitesData || []);

	const [newSiteList, setNewSiteList] = useState<SiteOption[]>([]);
	const [newLocationList, setNewLocationList] = useState<LocationOption[]>([]);
	const [isCancelTestDialogOpen, setIsCancelTestDialogOpen] = useState(false);
	const [isSpinnerOpen, setIsSpinnerOpen] = useState(false);
	const [globalSiteValue, setGlobalSiteValue] = useState<SiteOption | null>(null);
	const [globalLocation, setGlobalLocation] = useState<BCALocaton[]>([]);
	const [globalLocationValue, setGlobalLocationValue] = useState<LocationOption | null>(null);
	const globalLocationInputRef = useRef<HTMLInputElement | null>(null);

	const closeCancelTestDialog = () => {
		setIsCancelTestDialogOpen(false);
	};
	const openCancelTestDialog = () => {
		setIsCancelTestDialogOpen(true);
	};

	const closeSpinner = () => {
		setIsSpinnerOpen(false);
	};
	const openSpinner = () => {
		setIsSpinnerOpen(true);
	};

	const dispatch = useDispatch();
	const reduxState = useSelector((state: IReduxState) => state.user);
	const userData = reduxState?.userInformation;
	const sendPopUpMessage = usePopupMessage();
	const [importingAll, setImportingAll] = useState(false);
	const [importingAny, setImportingAny] = useState(false);
	const [siteInputValue, setSiteInputValue] = useState('');
	const [locationInputValue, setLocationInputValue] = useState('');
	const serialNumber = localStorage.getItem('analyzerSerialNumber') ?? '';
	let getAllCommandSent = false;

	const allSiteNames = React.useMemo(() => {
		let siteList = allSitesList
			.map((site) => ({
				name: site.name,
				id: site.id,
				locations: site.sampleLocationAllVOs?.map((loc) => ({
					name: loc.name,
					id: loc.id
				})) || []
			}));

		return siteList.sort((a, b) => a.name.localeCompare(b.name));

	}, [allSitesList]);

	const getLocationsForSite = useCallback((siteName: string): LocationOption[] => {
		const site = allSiteNames.find((s) => s.name === siteName);
		return site?.locations || [];
	}, [allSiteNames]);

	const locationsBySiteName = React.useMemo(() => {
		const map = new Map<string, LocationOption[]>();
		allSiteNames.forEach(site => map.set(site.name, site.locations));
		return map;
	}, [allSiteNames]);

	const filterSiteOptions = useCallback((options: SiteOption[], params: FilterOptionsState<SiteOption>): SiteOption[] => {
		options = options.concat(newSiteList);

		let filtered: SiteOption[] = [];

		if (params.inputValue !== '' && !options.find(option =>
			option.name.toLowerCase() === params.inputValue.toLowerCase()
		)) {
			filtered.push({
				name: params.inputValue,
				id: new Date().getTime(),
				inputValue: params.inputValue,
				locations: []
			});
		}

		filtered = filtered.concat(options.filter(option =>
			option.name.toLowerCase().includes(params.inputValue.toLowerCase())
		));

		return filtered;
	}, [newSiteList]);

	const filterLocationOptions = useCallback((
		options: LocationOption[],
		params: FilterOptionsState<LocationOption>,
		siteId?: number
	) => {	

		let filtered: LocationOption[] = [];

		if (params.inputValue !== '' &&
			!options.find(option => option.name.toLowerCase() === params.inputValue.toLowerCase())) {
			filtered.push({
				name: params.inputValue,
				id: new Date().getTime(),
				inputValue: params.inputValue
			});
		}

		filtered = filtered.concat(options.filter(option =>
			option.name.toLowerCase().includes(params.inputValue.toLowerCase()) &&
			(option.siteId !== undefined ? Number(siteId) === option.siteId : true)
		));

		return filtered;
	}, [newLocationList]);

	const updateSiteOptionList = useCallback((value: any) => {
		if (!('inputValue' in value)) return;
		const name = value.name;
		delete value.inputValue;
		setNewSiteList(prev => {
			if (prev.some(s => s.name.toLowerCase() === name.toLowerCase())) return prev;
			return [...prev, value];
		});
	}, []);

	const updateLocationOptionList = useCallback((value: any, siteId: any) => {
			const siteIdNum = Number(siteId);
		setNewLocationList(prev => {
			const isDuplicate = prev.some(
				loc => loc.name.toLowerCase() === value.name.toLowerCase() && loc.siteId === siteIdNum
			);
			if (isDuplicate) return prev;
			const entry = { name: value.name, id: value.id ?? new Date().getTime(), siteId: siteIdNum };
			return [...prev, entry];
		});
	}, []);

	const [deviceResults, setDeviceResults] = useState<DeviceResult[]>([]);

	const [fullData, setFullData] = useState<string[]>([]);
	let tempFullData: string[] = [];
	let targetNumberOfTests = 0;
	let numberOfTests = 0;
	let lastExpectedLines = -1;

	//See BCA docs for format of data
	function determineFullDataStoppingPoint(response: string): boolean {
		
		//CORRUPT responses count towards the total
		if (response.includes(',KIT:') || response.includes('CORRUPT ')) {
			numberOfTests++;
		}

		if (lastExpectedLines > -1) {
			lastExpectedLines--;
		}

		if (numberOfTests === targetNumberOfTests && lastExpectedLines === -1) {
			lastExpectedLines = response.includes('RLU only') ? 3 : 4;
			if (response.includes('CORRUPT ')) {
				lastExpectedLines = 0;
			}
		}

		if (lastExpectedLines === 0) { 
			lastExpectedLines = -1;
			numberOfTests = 0;
			return true;
		}

		return false;

	}

	function formatAnalyzerDate(dateString: string, result: DeviceResult): void {
		let date = new Date((Number.parseInt(dateString)*1000) + 1735704000000); //1735704000000 is where analyzer's date starts (in ms), see docs for more
		result.timestamp = date.getTime();
		result.date = date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
	}

	useEffect(() => {
		let results: DeviceResult[] = [];
		let result: DeviceResult = {
			id: 0,
			date: '',
			timestamp: 0,
			testKit: '',
			imported: false,
			notes: '',
			inputs: {},
			outputs: {},
			site: {id: -1, name: ''},
			location: {id: -1, name: ''}
		};
		let lineParts: string[];
		let idCounter = 0;

		for (let i = 0; i < fullData.length;) {

			if (fullData[i].includes("RLU only")) {
				i+=4;
				continue;

			} else if (fullData[i].startsWith('TIME')) {
				lineParts = fullData[i].split(',');
				formatAnalyzerDate(lineParts[0].substring(5), result);
				result.testKit = lineParts[1].substring(4); //TODO: need to run this through a test kit name map

			} else if (fullData[i].startsWith('INPUTS') && i+1 < fullData.length) {
				lineParts = fullData[i+1].split(',');
				for (const part of lineParts) {
					const [desc, value] = part.split(':');
					if (!desc.includes('?')) {
						result.inputs[desc] = (Number.isNaN(Number(value)) ? 'N/A' : value); //TODO: need to run desc through input name map
					}
				}

			} else if (fullData[i].startsWith('RESULTS') && i+1 < fullData.length) {
				lineParts = fullData[i+1].split(',');
				for (const part of lineParts) {
					const [desc, value] = part.split(':');
					result.outputs[desc] = (Number.isNaN(Number(value)) ? 'N/A' : value); //TODO: need to run desc through output name map
				}

				results = results.concat(result);
				idCounter++;
				result = {
					id: idCounter,
					date: '',
					timestamp: 0,
					testKit: '',
					imported: false,
					notes: '',
					inputs: {},
					outputs: {},
					site: {id: -1, name: ''},
					location: {id: -1, name: ''}
				};
			}

			i++;
		}

		results.sort((a, b) => b.timestamp - a.timestamp);

		setDeviceResults(results);

	}, [fullData]);

	async function handleAnalyzerData(response: any) {

		if (response.startsWith('N saved test')) {
			targetNumberOfTests = Number.parseInt(response.substring(14));
			tempFullData = [];
			if (targetNumberOfTests === 0) {
				setFullData([]);
				responseEmitter.removeListener('data', handleAnalyzerData);
			}
			return;
		}

		if (
			tempFullData.at(-1) !== response &&
			!response.includes("getDeviceInfo") &&
			!response.includes("getAllStored") &&
			!response.includes("bad command") &&
			!(serialNumber !== '' && response.includes(serialNumber))
		) {
			tempFullData = [...tempFullData, response];
			let fullDataReceived = determineFullDataStoppingPoint(response);

			if (fullDataReceived) { 
				setFullData(tempFullData);
				responseEmitter.removeListener('data', handleAnalyzerData);
			}

		}

	}

	useEffect(() => {
		if (!getAllCommandSent) {
			getAllCommandSent = true;
			responseEmitter.on('data', handleAnalyzerData);
			sendAnalyzerCommand('getAllStoredTests\n');
		}
	}, [])

	async function handleSave (toCSV = false) {
		openSpinner();
		let importedCount = undefined;
		try{
			let importSyncObject: any = []; 
			let groupSyncs: any = [];
			let siteSyncs: any = [];
			let locationSyncs: any = [];
			let dataPointSyncs: any = [];

			let stopProcessing = false;

			let newSites: boolean = false;
			let newSiteIDMap: Record<string, number> = {};

			let kitDetails: any = null;
			let translatedKitName: string = '';
			let kitId: number | null = null;
			let fieldDetails: any = null;
			let hasSpecialFieldCases: boolean = false;

			const allSitesCache = await getTestResults(`allSites`, localStorage.getItem('currentUser') ?? 'unknown');
			const defaultKitInfo = await getTestResults('defaultTestKits', localStorage.getItem('currentUser') ?? 'unknown');

			const groupsInfo = await getTestResults('groupsInfo', localStorage.getItem('currentUser') ?? 'unknown');

			const siteIDs = allSitesCache ? allSitesCache.map((site: any) => site.id) : [];
			const locationIDs = allSitesCache ? allSitesCache.flatMap((site: any) => site.sampleLocationAllVOs.map((loc: any) => loc.id)) : []; 

			const siteIDToDefaultLocationIDMap: Record<number, number> = populatingExistingDefaultLocations(allSitesCache);

			let firstSiteName = '';
			let uniqueSites = 0;

			let importedResults: DeviceResult[] = [];

			const importGroup = groupsInfo ? groupsInfo.find((group: any) => group.defaultType === 'Analyzer_Default') : null;
			const importGroupID = importGroup ? importGroup.id : Date.now();
			if (!importGroup) {
				groupSyncs = [{
					id: importGroupID,
					name: "Imported (ungrouped)",
					action: "BCA_DEFAULT_CREATE"
				}]
			}

			for (const result of deviceResults) {

				if (result.imported) {

					if (result.site.name === firstSiteName) {
						if (firstSiteName === '' && uniqueSites === 0) {
							uniqueSites++;
						}
					} else {
						firstSiteName = result.site.name;
						uniqueSites++;
					}

					if (uniqueSites > 1 && toCSV) {
						stopProcessing = true;
						sendPopUpMessage('Error', 'Only one site is allowed for CSV exports. Please use only one site across each imported result, or leave all sites blank.');
						break;
					}

					if (!siteIDs.includes(result.site.id)) {
						newSites = true;
						siteIDs.push(result.site.id);
						newSiteIDMap[result.site.name] = result.site.id;
						siteSyncs = siteSyncs.concat({
							groupFK: importGroupID,
							id: result.site.id,
							name: result.site.name === '' ? "Import: " + dayjs().format("MM/DD/YYYY HH:mm:ss") + '' : result.site.name,
							sortOrder: 9999,
							action: "CREATE"
						})
					}

					kitDetails = matchTestKit(result.testKit, defaultKitInfo);
					translatedKitName = kitDetails.name;
					kitId = defaultKitInfo.find((kit: any) => kit.testKitName === translatedKitName)?.id;
					fieldDetails = kitDetails.fieldDescriptorAllVOs;
					hasSpecialFieldCases = kitDetails.hasSpecialFieldCases;
					const isNoLocation = (result.location?.id == null || result.location.id === -1);
					let resolvedLocationId = -1;
					if (isNoLocation) {
						if (siteIDToDefaultLocationIDMap[result.site.id] !== undefined) {
							resolvedLocationId = siteIDToDefaultLocationIDMap[result.site.id];
						} else {
							resolvedLocationId = result.timestamp;
						}
					} else {
						resolvedLocationId = result.location!.id;
					}
					const alreadyTracked = (!isNoLocation || siteIDToDefaultLocationIDMap[result.site.id] !== undefined) && locationIDs.includes(resolvedLocationId);
					if (!alreadyTracked) {
						locationIDs.push(resolvedLocationId);
						if (!(result.location?.name && result.location.name !== '')) {
							siteIDToDefaultLocationIDMap[result.site.id] = resolvedLocationId;
						}
						locationSyncs = locationSyncs.concat({
							siteFK: result.site.id,
							id: result.location?.id === -1 || result.location?.id === undefined ? result.timestamp : result.location?.id,
							name: result.location?.name && result.location.name !== ''
								? result.location.name
								: "Imported Tests",
							testKitNames: [translatedKitName],
							action: "CREATE"
						});
					}

					let valueDatas: any = [];
					let outputId: string = '';
					for (const [key, value] of Object.entries(result.inputs)) {
						outputId = processFieldID(key, fieldDetails);
						let resultValue = processValue(value, outputId, result.testKit)
							valueDatas = valueDatas.concat({
								outputId: outputId,
								outputValue: resultValue,
								outputAlarm: processFieldAlarm(resultValue, outputId, kitId, resolvedLocationId, allSitesCache, defaultKitInfo),
								type: "Input"
							});
					}
					for (const [key, value] of Object.entries(result.outputs)) {
						outputId = processFieldID(key, fieldDetails)
						let resultValue = processValue(value, outputId, result.testKit)
							valueDatas = valueDatas.concat({
								outputId: outputId,
								outputValue: resultValue,
								outputAlarm: processFieldAlarm(resultValue, outputId, kitId, resolvedLocationId, allSitesCache, defaultKitInfo),
								type: "Output"
							});
					}

					if (hasSpecialFieldCases) {
						valueDatas = processSpecialFieldCases(valueDatas, result.testKit);
					}

					dataPointSyncs = dataPointSyncs.concat({
						locationFK: resolvedLocationId,
						operatorId: userData?.id,
						testKitId: kitId,
						note: result.notes ?? '',
						timestamp: result.timestamp,
						action: "ANALYZER_IMPORT_" + serialNumber,
						valueDatas: valueDatas
					})
					
					importedResults.push(result);

				}
			}

			if (stopProcessing) {
				return;
			}

			if (!newSites) {
				groupSyncs = [];
			}

			importSyncObject = {
				groupSyncs,
				siteSyncs,
				locationSyncs,
				dataPointSyncs
			}

			if (toCSV) {
				createAnalyzerCSV(importSyncObject, importedResults);
			}


			let syncResponse = await ApiService.uploadSyncData(importSyncObject);
			importedCount = importedResults.length;

			if (syncResponse) {
				sendPopUpMessage('Success', 'Test results imported successfully!');
			} else {
				sendPopUpMessage('Failure', 'There was an error importing test results. Please check your internet connection and try again.');
				closeSpinner();
				return;
			}

			//use getAllData API call to update cache, then close modal
			const sites = await ApiService.getAllDataNew(userData?.id ?? -1);
			const allSites = sites?.data?.groupAllVOs?.find(
				(group: { name: string }) => group.name === 'ALL SITES'
			);

			dispatch(setAllSitesData(allSites?.siteAllVOs));

			await saveTestResults(
				`allSites`,
				allSites?.siteAllVOs,
				userData?.email ?? ''
			);

			//update groups (will call getGroups too)
			dispatch(setUpdateReduxData(true));
		}
		finally{
			closeSpinner();
		}
		handleClose(importedCount);

	}

	async function createAnalyzerCSV(importObject: any, importedRawResults: any) {

		try {

			const username = localStorage.getItem("currentUser") ?? "unknown";
			const cacheUnits = await getTestResults("allDataPointsUnits", username);
			const defaultKitInfo = await getTestResults('defaultTestKits', localStorage.getItem('currentUser') ?? 'unknown');
			const units = { ...cacheUnits[0] };
			const incomingData = importObject.dataPointSyncs;
			let dataPoints: any[] = [];

			let dataPoint: any;
			let rawResult: DeviceResult;
			let currentKit: any;

			for (let i = 0; i < importedRawResults.length; i++) {

				dataPoint = incomingData[i];
				rawResult = importedRawResults[i];
				currentKit = defaultKitInfo?.find((kit: any) => kit.id === dataPoint.testKitId);

				dataPoint.valueDatas.forEach((valueData: any) =>

					dataPoints.push({
						samplePointName: rawResult.location?.name === '' ? dayjs(rawResult.timestamp).format("MM/DD/YYYY HH:mm:ss") + '' : rawResult.location?.name,
						testkitName: currentKit.testKitName,
						notes: dataPoint.note,
						outputId: valueData.outputId,
						timestamp: dataPoint.timestamp,
						siteId: rawResult.site.id === -1 ? importedRawResults[0].timestamp + '' : rawResult.site.id,
						siteName: rawResult.site.name === '' ? dayjs().format("MM/DD/YYYY HH:mm:ss") + '' : rawResult.site.name,
						outputValue: valueData.outputValue,
						displayName: currentKit.fieldDescriptorAllVOs.find((desc: any) => desc.outputId === valueData.outputId).outputName,
						testKitId: dataPoint.testKitId,
						unit: units[dataPoint.testKitId]?.[valueData.outputId]?.unit ?? "-",
					})

				)

			}

			const groupedByDate: { [key: string]: any[] } = {};
			dataPoints.forEach((dp) => {
				const dateKey = dp.timestamp ? dayjs(dp.timestamp).format("MM/DD/YYYY HH:mm:ss.SSS") : "";
				if (!groupedByDate[dateKey]) {
					groupedByDate[dateKey] = [];
				}
				groupedByDate[dateKey].push(dp);
			});

			const headersMap = new Map<string, string>();
			const subHeadersMap = new Map<string, string>();
			const unitsMap = new Map<string, string>();

			dataPoints.forEach((dp) => {
				const headerKey = `${dp.samplePointName}--${dp.testkitName}--${dp.outputId} (${dp.testKitId})`;
				headersMap.set(headerKey, dp.displayName);
				subHeadersMap.set(headerKey, `${dp.samplePointName}--${dp.testkitName}--${dp.outputId}`);
				unitsMap.set(headerKey, dp.unit || "-");
			});

			const headers = [
				"Site id#" + dataPoints[0]?.siteId,
				...Array.from(headersMap.values()),
				"Notes"
			];

			const subHeaderRow = [
				"Site name#" + dataPoints[0]?.siteName,
				...Array.from(subHeadersMap.values()),
				"Notes"
			];

			const unitsRow = [
				"MM/dd/yyyy HH:mm:ss.SSS",
				...Array.from(unitsMap.values()),
				"-"
			];

			let csvContent = "sep=,\n" + headers.join(",") + "\n" + subHeaderRow.join(",") + "\n" + unitsRow.join(",") + "\n";

			Object.keys(groupedByDate)
				.sort((a, b) => a.localeCompare(b))
				.forEach((dateKey) => {
					const dataRow = groupedByDate[dateKey];
					const row = Array.from(headersMap.keys()).map((headerKey) => {
						const dp = dataRow.find(
							(dp) => `${dp.samplePointName}--${dp.testkitName}--${dp.outputId} (${dp.testKitId})` === headerKey
						);
						return dp?.outputValue || "";
					});

					const uniqueNotes = new Set(dataRow.map((dp) => dp.notes).filter(Boolean));
					const notes = Array.from(uniqueNotes).join(" ");

					csvContent += [dateKey, ...row, notes].join(",") + "\n";
				});

			//specific timestamp formatting needed for file name e.g. 'Mar12_2024_6_34_54_PM'
			const dateObj = new Date(importedRawResults[0].timestamp);
			const monthShort = dateObj.toLocaleString('en-US', { month: 'short' });
			const day = dateObj.getDate();
			const year = dateObj.getFullYear();
			let hour = dateObj.getHours();
			const minute = dateObj.getMinutes().toString().padStart(2, '0');
			const second = dateObj.getSeconds().toString().padStart(2, '0');
			const isPM = hour >= 12;
			const ampm = isPM ? 'PM' : 'AM';
			hour = hour % 12;
			if (hour === 0) hour = 12;
			const timestampFileName = `${monthShort}${day}_${year}_${hour}_${minute}_${second}_${ampm}`;

			const siteNameOnReport = importedRawResults[0].site.name === '' ? timestampFileName : importedRawResults[0].site.name;
			handleDownloadCSV(csvContent, siteNameOnReport);

		} catch (error) {
			sendPopUpMessage('Failure', 'There was an error creating the CSV file.');
			console.error("Error creating CSV:", error);
		}

	}

	function importAll (importing: boolean) {
		setImportingAll(importing);
		setImportingAny(importing);

		for (const result of deviceResults) {
			updateResultField(result.id, "imported", importing);
		}
	}
	function handleClose (importedCount?: number) {
		responseEmitter.removeListener('data', handleAnalyzerData);
		getAllCommandSent = false;
		onClose(importedCount);
	};

	function updateAllResultFields<K extends keyof DeviceResult>(field: K, value: DeviceResult[K]) {
		setDeviceResults((prev) => {
			return prev.map((deviceResult) => {
			const updated = {
				...deviceResult,
				[field]: value,
			};

			if (field === "site") {
				const site = value as DeviceResult["site"];

				if (site) {
				setGlobalLocation(getLocationsForSite(site.name));
				} else {
				setGlobalLocation([]);
				}
				updated.location = null;
			}

			return updated;
			});
		});
	}

	const handleGlobalSiteChange = useCallback((_: any, newValue: SiteOption | string | null) => {
		setGlobalLocationValue(null);
		setLocationInputValue('');
		if (typeof newValue === 'string') {
			const allOptions = [...allSiteNames, ...newSiteList];
			const existing = allOptions.find(s => s.name.toLowerCase() === newValue.toLowerCase());
			if (existing) {
				setGlobalSiteValue(existing);
				updateAllResultFields('site', { name: existing.name, id: existing.id });
			} else {
				const newSite: SiteOption = { name: newValue, id: Date.now(), locations: [] };
				setNewSiteList(prev => [...prev, newSite]);
				setGlobalSiteValue(newSite);
				updateAllResultFields('site', { name: newValue, id: newSite.id });
			}
		} else if (newValue && 'name' in newValue) {
			setGlobalSiteValue(newValue);
			updateSiteOptionList(newValue);
			updateAllResultFields('site', { name: newValue.name, id: newValue.id });
		} else {
			setGlobalSiteValue(null);
			updateAllResultFields('site', { name: '', id: -1 });
		}
	}, [allSiteNames, newSiteList, updateSiteOptionList, updateAllResultFields]);

	const handleGlobalLocationChange  = useCallback((_: any, newValue: LocationOption | string | null) => {
		const currentOptions = [
			...(globalSiteValue?.name ? (locationsBySiteName.get(globalSiteValue.name) ?? []) : []),
			...newLocationList.filter(loc => loc.siteId === globalSiteValue?.id)
		];
		if (typeof newValue === 'string') {
			const existing = currentOptions.find(loc => loc.name.toLowerCase() === newValue.toLowerCase());
			if (existing) {
				const cleanValue = { name: existing.name, id: existing.id };
				setGlobalLocationValue(cleanValue);
				updateAllResultFields('location', cleanValue);
			} else {
				const newLoc = { name: newValue, id: new Date().getTime(), siteId: Number(globalSiteValue?.id) };
				setGlobalLocationValue(newLoc);
				setNewLocationList(prev => [...prev, newLoc]);
				updateAllResultFields('location', { name: newValue, id: newLoc.id });
			}
		} else if (newValue && 'name' in newValue) {
			//they clicked Add, extract values out
			const cleanValue = { name: newValue.name, id: newValue.id };
			setGlobalLocationValue(cleanValue);
			if ('inputValue' in newValue) {
				updateLocationOptionList(newValue, globalSiteValue?.id);
			}
			updateAllResultFields('location', { name: newValue.name, id: newValue.id });
		} else {
			setGlobalLocationValue(null);
			updateAllResultFields('location', null);
		}
	}, [globalSiteValue, locationsBySiteName, newLocationList, updateLocationOptionList, updateAllResultFields]);


	const updateResultField = useCallback(<K extends keyof DeviceResult>(resultId: number, field: K, value: DeviceResult[K]) => {
		if (field === 'site') {
			setGlobalSiteValue(null);
			setGlobalLocationValue(null);
			setSiteInputValue('');
			setLocationInputValue(''); 
		}
		if (field === 'location') setGlobalLocationValue(null);
		setLocationInputValue('');
		setDeviceResults((prev) => {
			const updated = [...prev];
			const resultIndex = updated.findIndex((r) => r.id === resultId); // moved inside
			if (field === 'site') updated[resultIndex].location = { id: -1, name: '' };
			updated[resultIndex] = { ...updated[resultIndex], [field]: value };

			if (field === 'imported') {
				const anyImported = updated.some(r => r.imported);
				const allImported = updated.every(r => r.imported);
				setImportingAny(anyImported);
				setImportingAll(allImported);
			}

			return updated;
		});
	}, []);

	return (
		<Dialog
			open={true}
			onClose={(event, reason) => {
				if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
					handleClose();
				}
			}}
			fullWidth
			maxWidth="md"
			id="main-menu"
			sx={{
				'& .MuiPaper-root': {
					overflow: 'hidden',
					maxHeight: '82vh',
					position: 'absolute',
					top: '50px',
					maxWidth: '1000px',
				},
			}}
		>
			<DialogTitle>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Text addStyle="!font-inter !font-bold !text-[#2C2C2C] !text-[24px] !mb-4">
						Bugcount Analyzer Import
					</Text>
					<IconButton
					onClick={importingAny ? openCancelTestDialog : () => handleClose()}
						sx={{
							position: 'absolute',
							top: 24,
							right: 24,
							p: 0,
							zIndex: 1000,
						}}
						aria-label="close"
					>
						<img
							src="/assets/icons/Close.svg"
							alt="Close"
							style={{
								width: '20px',
								height: '20px',
							}}
						/>
					</IconButton>
				</div>
				<Text>
					Serial Number: {serialNumber}
				</Text>
				<Box display="flex" alignItems="center" gap={1} sx={{ paddingTop: '0px', paddingBottom: '6px', paddingX: '8px', borderRadius: '4px', width: 'fit-content', mt: 1 }}>
						<Checkbox
							checked={importingAll}
							onChange={(e) => importAll(e.target.checked)}
						/>
						<Text>Import All</Text>
					<Box display="flex" alignItems="center" gap={1} sx={{ paddingTop: '8px', paddingBottom: '8px', paddingX: '8px', borderRadius: '4px', width: 'fit-content', mt: 1 }}>
						<Box minWidth={200}>
							{/* Global site dropdown */}
							<TabbableAutocomplete<SiteOption>
								value={globalSiteValue}
								inputValue={siteInputValue}
								options={allSiteNames}
								filterOptions={filterSiteOptions}
								onChange={handleGlobalSiteChange}
								onInputChange={setSiteInputValue}
								nextFocusRef={globalLocationInputRef}
							/>
						</Box>
						<Box minWidth={200}>
							{/* Global Location dropdown */}
							<TabbableAutocomplete<LocationOption>
								value={globalLocationValue}
								inputValue={locationInputValue}
								options={[
									...(globalSiteValue?.name ? (locationsBySiteName.get(globalSiteValue.name) ?? []) : []),
									...newLocationList.filter(loc => loc.siteId === globalSiteValue?.id)
								]}
								filterOptions={(options, params) => filterLocationOptions(options, params, globalSiteValue?.id)}
								onChange={handleGlobalLocationChange}
								onInputChange={setLocationInputValue}
								freeSolo
								disabled={globalSiteValue === null}
								placeholder="Select a Site first"
								inputRef={globalLocationInputRef}
							/>
						</Box>
					</Box>
				</Box>
				<Text>
					If no site is chosen, a new site named with today's date will be created
				</Text>
			</DialogTitle>
			<DialogContent>
				<table style={{ width: '100%', borderCollapse: 'collapse' }}>

					<thead>
						<tr style={{ position: 'sticky', top: 0, background: '#f5f5f5', zIndex: '3' }}>
							<th style={{ margin: 'auto', fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>Import?</th>
							<th style={{ margin: 'auto', fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>Date</th>
							<th style={{ margin: 'auto', fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>Test Kit</th>
							<th style={{ margin: 'auto', fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>Site</th>
							<th style={{ margin: 'auto', fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>Sample Location</th>
							<th style={{ margin: 'auto', fontSize: '0.825rem', fontWeight: 500, padding: '8px' }}>Notes</th>
						</tr>
					</thead>

					<tbody>
						{deviceResults.map((result) => (
							<ResultRow
								key={result.id}
								result={result}
								newLocationList={newLocationList}
								allSiteNames={allSiteNames}
								locationsBySiteName={locationsBySiteName}
								filterSiteOptions={filterSiteOptions}
								filterLocationOptions={filterLocationOptions}
								onUpdateField={updateResultField}
								onUpdateSiteOptionList={updateSiteOptionList}
								onUpdateLocationOptionList={updateLocationOptionList}
							/>
						))}
					</tbody>

				</table>
			</DialogContent>
			<DialogActions>
				<Box display="flex" justifyContent="space-between" alignItems="center" width="100%" padding={'8px'} paddingTop={'0px'}>
					<Button
						onClick={() => handleSave(true)}
						color="primary"
						className={((!importingAny && !importingAll) || !navigator.onLine) ? "!text-[#9EACB4]" : "!text-[#0092C8]"}
						disabled={(!importingAny && !importingAll) || !navigator.onLine}
					>
						SAVE AND EXPORT TO CSV
					</Button>

					<Box display="flex" alignItems="center" gap={1} paddingRight={'8px'}>
						<Button
							onClick={importingAny ? openCancelTestDialog : () => handleClose()}
							color="primary"
							className="!text-[#9EACB4]"
						>
							CANCEL
						</Button>
						<PrimaryButton
							addStyle="!h-[36px] !w-[68px]"
							disabled={(!importingAny && !importingAll) || !navigator.onLine}
							onClick={() => handleSave(false)}
						>
							SAVE
						</PrimaryButton>
					</Box>
				</Box>
			</DialogActions>
			<CancelConfirmationDialog
				open={isCancelTestDialogOpen}
				onClose={closeCancelTestDialog}
				onConfirm={handleClose}
			/>
			<Spinner 
				open={isSpinnerOpen}
				onClose={closeSpinner} 
				onConfirm={closeSpinner}
			/>
		</Dialog>
	);
};







export default ImportModal;
